import { DoubleChevronLeft8Icon } from '@fluentui/react-icons-mdl2';
import {
  Dropdown,
  DropdownProps,
  Flex,
  Input,
  SearchIcon,
  Skeleton
} from "@fluentui/react-northstar";
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomViewsDropdown } from '../../../tikit/List/CustomViewsDropdown';
import { listInputItems } from '../../../tikit/ticketHelper';
import { setViewIdsInStorage } from '../../../tikit/toolbar/CustomViewPopup/heplers/customPopupHelper';
import { EmptyData } from '../EmptyData';
import { TicketCard } from '../TicketCard/TicketCard';
import { TicketCardSkeleton } from '../TicketCard/TicketCardSkeleton';
import FlashlightSVG from './../../../../svg/flashlight.svg';
import Styles from './EditTicketsView.module.css';

interface Props {
  onTicketClick: (ticketId: number) => void;
  selectedTicketId: number;
  activeDrodpdownIndex: number;
  setActiveDrodpdownIndex: (index: number) => void;
  ticketsState: SideViewTicketState;
  setTicketsStateHandler: React.Dispatch<
    React.SetStateAction<SideViewTicketState>
  >;
  globalTheme: any;
  isCustomViewEnabled: boolean;
  isScopingEnabled: boolean;
  sortedViews: any[];
  currentUser: any;
}

export const EditTicketsView = ({
  onTicketClick,
  selectedTicketId,
  activeDrodpdownIndex,
  setActiveDrodpdownIndex,
  ticketsState,
  setTicketsStateHandler,
  globalTheme,
  isCustomViewEnabled,
  isScopingEnabled,
  sortedViews,
  currentUser
}: Props) => {
  const { t } = useTranslation();
  const observer = useRef<IntersectionObserver>();
  const isMount = useRef<boolean>();
  const [search, setSearch] = useState("");

  const [viewName, setViewName] = useState(t('ticket.parent-filter.all-active'));
  const [isLoadedFilter, setIsLoadedFilter] = useState<boolean>(false);

  useEffect(() => {
    if (!isCustomViewEnabled) {
      setIsLoadedFilter(true);
      return;
    }

    const savedCustomViewId = localStorage.getItem('selectedCustomViewId') ?? null;
    if (savedCustomViewId !== null) {
      const savedView = sortedViews.find(view => view.Id.toString() === savedCustomViewId);
      if (savedView) {
        setViewName(savedView.Name);
        setIsLoadedFilter(true);
      } else {
        setIsLoadedFilter(true);
      }
    } else {
      setIsLoadedFilter(true);
    }
  }, [sortedViews, isCustomViewEnabled])


  const lastTicketElementRef = useCallback(
    (node) => {
      if (ticketsState.loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && ticketsState.hasMore) {
          setTicketsStateHandler((pre) => ({
            ...pre,
            pageNumber: pre.pageNumber + 1,
          }));
        }
      });
      if (node) observer.current.observe(node);
    },
    [ticketsState.loading, ticketsState.hasMore]
  );

  const debounceSearch = useCallback(
    _.debounce((_searchText: string) => {
      setTicketsStateHandler((pre) => ({
        ...pre,
        pageNumber: 1,
        tickets: [],
        searchText: _searchText,
      }));
    }, 1000),
    []
  );

  const hiddenClass = "-left-20 w-0";
  const showClass = "w-full left-0";
  const [showEditView, setShowEditView] = useState(true);

  const handleEditViewToggle = useCallback(() => {
    localStorage.setItem("editTicketViewCollapsed", (!showEditView).toString());
    setShowEditView(!showEditView);
  }, [showEditView, setShowEditView]);

  useEffect(() => {
    if (isMount.current) debounceSearch(search);
    isMount.current = true;
    const editViewCollapsed = localStorage.getItem("editTicketViewCollapsed");
    if (editViewCollapsed === "true" || editViewCollapsed === "false") {
      setShowEditView(editViewCollapsed === "true");
    } else {
      setShowEditView(true);
      localStorage.setItem("editTicketViewCollapsed", "true");
    }
  }, [search]);

  const onCardClick = useCallback(
    (id: number) => {
      onTicketClick(id);
    },
    [selectedTicketId, ticketsState]
  );

  const selectView = (customView: any, defaultiew: any) => {
    setViewName(customView.Name);
    localStorage.setItem('selectedCustomViewId', customView.Id.toString());
    setViewIdsInStorage({ customViewId: customView.Id.toString(), defaultViewId: defaultiew.Id.toString() })
    setActiveDrodpdownIndex(customView.Id);
    setTicketsStateHandler(pre => ({ ...pre, selectedViewId: customView.Id, pageNumber: 1, tickets: [] }));
  }

  const handleDropdownChange = useCallback((_event: any, props: DropdownProps) => {
    setActiveDrodpdownIndex(props.highlightedIndex);
    setTicketsStateHandler(pre => ({ ...pre, pageNumber: 1, tickets: [] }));
  }, [activeDrodpdownIndex, ticketsState]);

  return (
    <div className={`h-full relative ${Styles.EditTicket
      } hidden md:flex print-hidden items-center ${!showEditView ? "w-5 p-0" : "p-4"
      }`}>
      <div className={`h-full relative ${(showEditView ? showClass : hiddenClass)}`}>
        <div className={Styles.InputsContainer}>
          <Flex className="mb-3" hAlign="end">

            {!isCustomViewEnabled &&
              <Dropdown style={{ cursor: 'pointer' }}
                inline
                items={Object.values(listInputItems)}
                triggerButton={
                  <span className="font-semibold">
                    {listInputItems[activeDrodpdownIndex]}
                  </span>
                }
                placeholder={t('ticket-details.edit-ticket-view.dropdown-placeholder')}
                onChange={handleDropdownChange}
              />
            }
            {!isLoadedFilter &&
              <Skeleton.Button className={Styles.DropdownSkeleton} />
            }
            {isCustomViewEnabled && isLoadedFilter &&
              <CustomViewsDropdown
                showAddView={false}
                sortedViews={sortedViews}
                viewName={viewName}
                globalTheme={globalTheme}
                isCustomViewEnabled={isCustomViewEnabled}
                selectView={selectView}
                customCssClass={Styles.CustomViewDropdownList}
                isScopingEnabled={isScopingEnabled}
              />
            }

          </Flex>
          <Input
            placeholder={t('ticket-details.edit-ticket-view.search-placeholder')}
            icon={<SearchIcon />}
            iconPosition="end"
            inverted
            fluid
            defaultValue={search}
            onChange={useCallback((_: any, props: any) => { setSearch(props.value) }, [search, ticketsState])}
          />
        </div>
        <div style={{ height: 'calc(100vh - 230px)' }} className="overflow-auto">
          {ticketsState.tickets.length > 0 &&
            ticketsState.tickets.map((ticket: Ticket, index: number) =>
              ticketsState.tickets.length === index + 1 ? (
                <div key={ticket.Id} ref={lastTicketElementRef}>
                  <TicketCard
                    key={ticket.Id}
                    ticket={ticket}
                    selectedTicketId={selectedTicketId}
                    onClick={onCardClick}
                  />
                </div>
              ) : (
                <div key={ticket.Id}>
                  <TicketCard
                    ticket={ticket}
                    selectedTicketId={selectedTicketId}
                    onClick={onCardClick}
                  />
                </div>
              )
            )}
          {ticketsState.loading && (
            <div>
              <TicketCardSkeleton />
              <TicketCardSkeleton />
              <TicketCardSkeleton />
            </div>
          )}
          {!ticketsState.loading && ticketsState.tickets.length === 0 && (
            <EmptyData
              SVGIcon={<FlashlightSVG width={110} height={110} className={'mb-4'} />}
              headerText={t('ticket-details.edit-ticket-view.empty.header-text')}
              subheaderText={t('ticket-details.edit-ticket-view.empty.sub-header-text')}
            />
          )}

        </div>
      </div>
      <div className={`pb-4 mb-4 transform scale-60 absolute ${!showEditView ? "w-5 right-1" : "-right-1"}`}>
        <button onClick={handleEditViewToggle} className={`hover:opacity-100 opacity-50 p-2 transform transition-all ${showEditView ? "rotate-0" : "rotate-180"}`}>
          <DoubleChevronLeft8Icon />
        </button>
      </div>
    </div>
  );
};
